/**
 * SyncQueue - persistent mutation queue for offline support.
 *
 * Mutations that fail due to network errors are queued in localStorage
 * and replayed sequentially when connectivity is restored. Ordering is
 * preserved to maintain data consistency (e.g. create customer before
 * creating an invoice for that customer).
 */

const QUEUE_KEY = 'lc:queue'
const MAX_RETRIES = 3

export type MutationStatus = 'pending' | 'syncing' | 'failed'

export interface QueuedMutation {
  id: string
  method: string
  path: string
  body?: string
  headers?: Record<string, string>
  queuedAt: number
  retries: number
  status: MutationStatus
  description: string
  error?: string
}

export type DrainExecutor = (
  path: string,
  options: RequestInit,
) => Promise<unknown>

export class SyncQueue {
  /** Add a failed mutation to the queue. */
  enqueue(mutation: Omit<QueuedMutation, 'id' | 'queuedAt' | 'retries' | 'status'>): QueuedMutation {
    const queue = this.load()
    const entry: QueuedMutation = {
      ...mutation,
      id: crypto.randomUUID(),
      queuedAt: Date.now(),
      retries: 0,
      status: 'pending',
    }
    queue.push(entry)
    this.save(queue)
    return entry
  }

  /** Remove a mutation from the queue by ID. */
  dequeue(id: string): void {
    const queue = this.load().filter((m) => m.id !== id)
    this.save(queue)
  }

  /** Get the next pending mutation without removing it. */
  peek(): QueuedMutation | null {
    return this.load().find((m) => m.status === 'pending') ?? null
  }

  /** Get all queued mutations. */
  all(): QueuedMutation[] {
    return this.load()
  }

  /** Number of pending (unsent) mutations. */
  get pending(): number {
    return this.load().filter((m) => m.status === 'pending').length
  }

  /** Number of failed mutations. */
  get failed(): number {
    return this.load().filter((m) => m.status === 'failed').length
  }

  /** Total queue size (all statuses). */
  get size(): number {
    return this.load().length
  }

  /** Whether the queue has any pending items. */
  get isEmpty(): boolean {
    return this.pending === 0
  }

  /** Discard a specific failed mutation. */
  discard(id: string): void {
    this.dequeue(id)
  }

  /** Reset a failed mutation back to pending for retry. */
  retry(id: string): void {
    const queue = this.load()
    const entry = queue.find((m) => m.id === id)
    if (entry) {
      entry.status = 'pending'
      entry.retries = 0
      entry.error = undefined
      this.save(queue)
    }
  }

  /**
   * Drain the queue sequentially, replaying each pending mutation
   * through the provided executor function.
   *
   * @param executor - function that sends the request (typically ApiClient.request)
   * @param onProgress - called after each mutation is processed
   * @returns number of successfully synced mutations
   */
  async drain(
    executor: DrainExecutor,
    onProgress?: (remaining: number, total: number, current: QueuedMutation) => void,
  ): Promise<{ synced: number; failed: number }> {
    const queue = this.load()
    const pendingItems = queue.filter((m) => m.status === 'pending')
    const total = pendingItems.length
    let synced = 0
    let failedCount = 0

    for (const mutation of pendingItems) {
      // Mark as syncing
      mutation.status = 'syncing'
      this.save(queue)

      try {
        await executor(mutation.path, {
          method: mutation.method,
          body: mutation.body,
          headers: mutation.headers,
        })

        // Success - remove from queue
        const idx = queue.indexOf(mutation)
        if (idx >= 0) queue.splice(idx, 1)
        this.save(queue)
        synced++
      } catch (e) {
        mutation.retries++
        mutation.error = e instanceof Error ? e.message : String(e)

        if (mutation.retries >= MAX_RETRIES) {
          mutation.status = 'failed'
        } else {
          mutation.status = 'pending'
        }
        this.save(queue)
        failedCount++
      }

      onProgress?.(total - synced - failedCount, total, mutation)
    }

    return { synced, failed: failedCount }
  }

  /** Clear the entire queue. */
  clear(): void {
    localStorage.removeItem(QUEUE_KEY)
  }

  // --- Persistence ---

  private load(): QueuedMutation[] {
    const raw = localStorage.getItem(QUEUE_KEY)
    if (!raw) return []
    try {
      return JSON.parse(raw) as QueuedMutation[]
    } catch {
      return []
    }
  }

  private save(queue: QueuedMutation[]): void {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue))
  }
}
